import { FormControl, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup } from "@mui/material";
import { CSSProperties } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Close } from "@mui/icons-material";

/**
 * FormRadioGroupInputOption
 */

export type FormRadioGroupInputOption = {
  id: number;
  label: string;
  style?: CSSProperties;
  disabled?: boolean;
};

/**
 * FormRadioGroupInput
 */

type Props<T extends {}> = {
  name: keyof T;
  label?: string;
  required?: boolean;
  options: FormRadioGroupInputOption[];
  showCleanButton?: boolean;
  readOnly?: boolean;
  arrangement?: 'horizontal' | 'vertical';
  boldOptionsLabel?: boolean;
  reactHookForm: UseFormReturn<T>;
  onValueChange?: (value: number | null, prevValue: number | null) => void;
};

export const FormRadioGroupInput = <T extends {},>({
  name,
  label,
  required,
  options,
  showCleanButton,
  readOnly,
  arrangement,
  reactHookForm,
  boldOptionsLabel,
  onValueChange,
}: Props<T>) => {
  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let helperText = null;

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        return (
          <div className="w-full flex flex-col gap-2">
            <FormControl
              size="small"
              required={required}
              sx={{
                width: '100%',
                flexDirection: arrangement === 'horizontal' ? 'row' : 'column',
                justifyContent: arrangement === 'horizontal' ? 'space-between' : 'flex-start',
                alignItems: arrangement === 'horizontal' ? 'center' : 'flex-start',
              }}>
              {label &&
                <FormLabel
                  id={`input-radio-group-${name as string}`}
                  error={helperText !== null}
                  required={required}>
                  {label}
                </FormLabel>
              }

              <div className="flex flex-row items-center">
                <RadioGroup
                  aria-labelledby={`input-radio-group-${name as string}`}
                  name={field.name}
                  value={field.value ?? null}
                  row={true}
                  onChange={(e) => {
                    const value = field.value;
                    reactHookForm.setValue(name as any, Number(e.target.value) as any);
                    onValueChange?.(Number(e.target.value), Number(value));
                  }}>
                  {options.map(opt => (
                    <FormControlLabel
                      key={opt.id}
                      className="text-black dark:text-white"
                      value={opt.id}
                      disabled={readOnly || opt.disabled}
                      control={<Radio />}
                      label={
                        <span
                          className={`whitespace-nowrap ${boldOptionsLabel ? "font-bold" : ""}`}
                          style={opt.style}
                        >
                          {opt.label}
                        </span>
                      }
                    />
                  ))}
                </RadioGroup>
                {
                  (showCleanButton && field.value !== null && field.value !== undefined) &&
                  <div className="ml-2">
                    <span
                      className="flex flex-row items-center cursor-pointer text-xs text-[#009C4E] font-bold"
                      onClick={() => field.onChange(null)}>
                      <Close /> Limpar
                    </span>
                  </div>
                }
              </div>
            </FormControl>

            <FormHelperText id={`input-radio-group-${name as string}`} error={helperText !== null}>
              {helperText}
            </FormHelperText>
          </div>
        );
      }} />
  );
}